/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiBadge } from '../../badge';
import { EuiI18n } from '../../i18n';
export class EuiComboBoxPill extends Component {
  static defaultProps = {
    color: 'hollow'
  };
  onCloseButtonClick = () => {
    const {
      onClose,
      option
    } = this.props;

    if (onClose) {
      onClose(option);
    }
  };

  render() {
    const {
      asPlainText,
      children,
      className,
      color,
      onClick,
      onClickAriaLabel,
      onClose,
      // eslint-disable-line no-unused-vars
      option,
      // eslint-disable-line no-unused-vars
      ...rest
    } = this.props;
    const classes = classNames('euiComboBoxPill', {
      'euiComboBoxPill--plainText': asPlainText
    }, className);
    const onClickProps = onClick && onClickAriaLabel ? {
      onClick,
      onClickAriaLabel
    } : {};

    if (onClose) {
      return <EuiI18n token="euiComboBoxPill.removeSelection" default="Remove {children} from selection in this group" values={{
        children
      }}>
          {removeSelection => <EuiBadge className={classes} color={color} iconOnClick={this.onCloseButtonClick} iconOnClickAriaLabel={removeSelection} iconSide="right" iconType="cross" title={children} {...onClickProps} {...rest}>
              {children}
            </EuiBadge>}
        </EuiI18n>;
    }

    if (asPlainText) {
      return <span className={classes} {...rest}>
          {children}
        </span>;
    }

    return <EuiBadge className={classes} color={color} title={children} {...rest} {...onClickProps}>
        {children}
      </EuiBadge>;
  }

}
EuiComboBoxPill.propTypes = {
  asPlainText: PropTypes.bool,
  children: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  onClickAriaLabel: PropTypes.any,
  onClose: PropTypes.func,
  option: PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string
    }).isRequired),
    value: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiComboBoxPill.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
    description: '',
    displayName: 'EuiComboBoxPill',
    methods: [],
    props: {
      asPlainText: {
        defaultValue: null,
        description: '',
        name: 'asPlainText',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: {
          value: 'hollow'
        },
        description: '',
        name: 'color',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      onClickAriaLabel: {
        defaultValue: null,
        description: '',
        name: 'onClickAriaLabel',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClose: {
        defaultValue: null,
        description: '',
        name: 'onClose',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: false,
        type: {
          name: 'OptionHandler<T>'
        }
      },
      option: {
        defaultValue: null,
        description: '',
        name: 'option',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_pill.tsx',
          name: 'EuiComboBoxPillProps'
        }],
        required: true,
        type: {
          name: 'EuiComboBoxOptionOption<T>'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiComboBoxPillProps', 'CommonProps']
  };
} catch (e) {}